// Vendor -> survey param mappings and landing link endings.
//
// The mapping decides which params the vendor sends reach the survey link, and under
// which name. The landing link ending is fixed extras for this vendor's respondents
// (e.g. `or3=2` for one vendor), added to the survey link only, so the vendor never
// sees or changes it. buildSurveyLandingUrl mirrors what surveyengine does when it
// builds the survey link, so the previews here show what a respondent will land on.

import { UrlParamMapping } from '../models/url-param-mapping';

type EntryUrlLinks = {
  completeRedirectURL?: string;
  qcRedirectURL?: string;
  overQuotaRedirectURL?: string;
  terminateRedirectURL?: string;
  urlParamMappings?: UrlParamMapping[] | null;
};

export type QueryParam = { name: string; value: string };

// surveyengine fills these itself, so they never go on a vendor entry link.
export const builtInParams = ['or1', 'cleanid', 'fraudscore', 'orvid'];
const paramNamePattern = /^[A-Za-z0-9_]+$/;

export function normalizeLinkEnding(ending: string | null | undefined): string {
  return (ending ?? '').trim().replace(/^[?&]+/, '').replace(/&+$/, '');
}

export function parseQueryParams(ending: string | null | undefined): QueryParam[] {
  const normalized = normalizeLinkEnding(ending);
  if (!normalized) return [];

  return normalized.split('&')
    .filter(pair => pair.length > 0)
    .map(pair => {
      const idx = pair.indexOf('=');
      return idx < 0
        ? { name: pair, value: '' }
        : { name: pair.substring(0, idx), value: pair.substring(idx + 1) };
    });
}

// #tokens# in the query of a link, leaving out those inside the params named in skipParams.
function tokensInQuery(url: string | null | undefined, skipParams = new Set<string>()): string[] {
  const paramIdx = (url ?? '').indexOf('?');
  if (paramIdx <= 0) return [];
  return url.substring(paramIdx + 1).split('&')
    .filter(pair => !skipParams.has(pair.split('=')[0]))
    .flatMap(pair => Array.from(pair.matchAll(/#(.*?)#/g), match => match[1]));
}

// Everything after the `?` of a vendor entry link: params for the #tokens# the survey
// link and redirects use and for every mapped vendor param, then the panelist id. A
// mapped param is shown filled with its own name (`sessionId={sessionId}`) so the vendor
// can see what to pass in; the rest stay blank. A mapping makes some params unnecessary:
// - a token used only inside a survey link param that a mapping replaces
//   (`pid=#psid#` with `rid -> pid` needs no `psid`);
// - `s2`, when another vendor param maps into it: surveyengine reads the panelist id from
//   that param instead, so it takes the place of `s2`.
// The panelist id stays last because some vendors append their ID straight onto the end
// of the link.
export function buildEntryUrlParams(surveyLink: string | null | undefined, links: EntryUrlLinks): string {
  const mapped = new Set<string>();
  const mappedSurveyParams = new Set<string>();
  let idParam = 's2';
  (links.urlParamMappings ?? []).forEach(mapping => {
    const vendorParam = (mapping.vendorParam ?? '').trim();
    const surveyParam = (mapping.surveyParam ?? '').trim();
    if (urlParamNameError(vendorParam) || urlParamNameError(surveyParam)) return;
    mapped.add(vendorParam);
    mappedSurveyParams.add(surveyParam);
    if (surveyParam === 's2' && idParam === 's2' && !builtInParams.includes(vendorParam)) idParam = vendorParam;
  });
  const param = (name: string) => mapped.has(name) ? `${name}={${name}}` : `${name}=`;

  const tokens = [
    ...tokensInQuery(surveyLink, mappedSurveyParams),
    ...tokensInQuery(links.completeRedirectURL),
    ...tokensInQuery(links.qcRedirectURL),
    ...tokensInQuery(links.overQuotaRedirectURL),
    ...tokensInQuery(links.terminateRedirectURL)
  ].sort().reverse();

  const handled = new Set<string>(['s2', idParam, ...builtInParams]);
  const parts: string[] = [];
  [...tokens, ...mapped].forEach(name => {
    if (handled.has(name)) return;
    parts.push(param(name));
    handled.add(name);
  });

  parts.push(param(idParam));
  return parts.join('&');
}

export function urlParamNameError(name: string | null | undefined): string | null {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return 'Required';
  if (!paramNamePattern.test(trimmed)) return 'Letters, numbers and _ only';
  return null;
}

export function urlParamMappingErrors(mappings: UrlParamMapping[]): (string | null)[] {
  const seen = new Set<string>();
  return mappings.map(mapping => {
    const nameError = urlParamNameError(mapping.vendorParam) ?? urlParamNameError(mapping.surveyParam);
    if (nameError) return nameError;
    if (builtInParams.includes(mapping.surveyParam.trim())) return `${mapping.surveyParam.trim()} is reserved`;

    const key = `${mapping.vendorParam.trim()}>${mapping.surveyParam.trim()}`;
    if (seen.has(key)) return 'Duplicate mapping';
    seen.add(key);
    return null;
  });
}

// Landing link ending params that share a name (ignoring case) with a param the vendor sends on
// the entry link or a mapped survey param. The ending's value wins on the survey link and in
// participant exports, so the editor warns about these rather than blocking the save.
export function landingLinkEndingClashes(landingLinkEnding: string | null | undefined,
  entryParamNames: string[], mappings: UrlParamMapping[]): string[] {
  const taken = new Set([
    ...entryParamNames,
    ...(mappings ?? []).map(mapping => (mapping.surveyParam ?? '').trim())
  ].filter(name => name).map(name => name.toLowerCase()));

  const clashes = parseQueryParams(landingLinkEnding)
    .map(param => param.name)
    .filter(name => taken.has(name.toLowerCase()));
  return Array.from(new Set(clashes));
}

// A value the vendor's platform fills in (`%transid%`, `{session_id}`, `[%RID%]`) or
// left blank, rather than a fixed value we hand out.
export function isVendorPlaceholder(value: string): boolean {
  return value === '' || /[%{}\[\]#]/.test(value);
}

function replaceQueryParam(url: string, name: string, value: string): string | null {
  const queryIdx = url.indexOf('?');
  if (queryIdx < 0) return null;

  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`([?&]${escaped}=)[^&#]*`);
  const query = url.substring(queryIdx);
  if (!pattern.test(query)) return null;
  return url.substring(0, queryIdx) + query.replace(pattern, (_match, prefix: string) => prefix + value);
}

function setQueryParam(url: string, name: string, value: string): string {
  return replaceQueryParam(url, name, value) ?? url + (url.includes('?') ? '&' : '?') + `${name}=${value}`;
}

// Survey link a respondent lands on, given what the vendor sent. Existing #token#
// substitution runs first: a token the vendor did not send is blanked, as surveyengine
// does, and survey engine params are left as tokens for the caller to show. `#s2#` takes
// the value of a param mapped into s2, since that param is the panelist id. Mapped params
// then replace an existing `name=` in the survey link or are appended, and the landing
// link ending goes last, as typed, so its fixed values win.
export function buildSurveyLandingUrl(surveyLink: string, incoming: Record<string, string>,
  mappings: UrlParamMapping[], landingLinkEnding?: string): string {
  let url = surveyLink ?? '';

  const idMapping = (mappings ?? []).find(mapping => mapping.surveyParam === 's2' && incoming[mapping.vendorParam] !== undefined);
  const tokenValues = idMapping ? { ...incoming, s2: incoming[idMapping.vendorParam] } : incoming;
  const queryIdx = url.indexOf('?');
  if (queryIdx > 0) {
    const query = url.substring(queryIdx).replace(/#(.*?)#/g, (token, name: string) =>
      name in tokenValues ? tokenValues[name] : builtInParams.includes(name) ? token : '');
    url = url.substring(0, queryIdx) + query;
  }

  (mappings ?? []).forEach(mapping => {
    const value = incoming[mapping.vendorParam];
    if (value === undefined || !mapping.surveyParam) return;
    url = setQueryParam(url, mapping.surveyParam, encodeURIComponent(value));
  });

  parseQueryParams(landingLinkEnding).forEach(param => {
    url = setQueryParam(url, param.name, param.value);
  });

  return url;
}
